import React, { Fragment, useEffect, useLayoutEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { reduxForm, Field, change } from 'redux-form';
import { ButtonToolbar, Button, Label, Collapse } from 'reactstrap';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import validate from './Validate';
import renderField from '../../../shared/components/form/RenderField';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import { bankListByFilter } from '../../../shared/queries/Account';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import Select from '../../../shared/components/form/Select';
import GraphQL from '../../../GraphQL';
import { PERSON_TYPE } from '../../../shared/utils/constants';
import { cpfMasker, cnpjMasker } from '../../../shared/components/form/Masks';
import ownerTypeSelector from '../../../shared/components/form/OwnerTypeSelector';

const AccountForm = (props) => {
  const {
    t,
    dispatch,
    backHandler,
    handleSubmit,
    dto,
    initialize,
    showLoading,
    hideLoading,
  } = props;
  const [accountChange, setAccountChange] = useState(false);
  const [isType, setIsType] = useState(PERSON_TYPE.LEGAL_PERSON);
  const [listOfBanks, setListOfBanks] = useState([]);
  const [masker, setMaskers] = useState({});
  const [ownerId, setOwnerId] = useState(true);

  const loadAllBank = () => {
    const filter = { filterByStatus: true, active: true, updaterId: 0 };
    const query = bankListByFilter(filter);
    showLoading();
    GraphQL(query, {})
      .then((res) => {
        res.bankListByFilter = res.bankListByFilter.map((item) => ({
          ...item,
          value: parseInt(item.id, 10),
          label: item.name,
        }));
        setListOfBanks(res.bankListByFilter);
        hideLoading();
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  useEffect(() => {
    initialize(dto);
  }, [dto]);

  useLayoutEffect(() => {
    if (accountChange) {
      dispatch(change('AccountForm', 'ownerType', PERSON_TYPE.LEGAL_PERSON));
    }
  }, [accountChange]);

  useEffect(() => {
    loadAllBank();
    if (dto.checkingAccount) {
      setAccountChange(true);
    }
  }, []);

  return (
    <Fragment>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.name')}</span>
          <div className="form__form-group-field">
            <Field
              name="name"
              type="text"
              component={renderField}
              maxLength="100"
              placeholder={t('account.placeholders.name')}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">
            {t('general.description')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="description"
              type="text"
              component="textarea"
              maxLength="500"
              placeholder={t('account.placeholders.description')}
            />
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group-field">
            <Label className="form__form-group-label pr-2">
              {t('general.active')}
            </Label>
            <Field
              name="active"
              component={renderCheckBoxField}
              className="colored-click"
            />
            <Label className="form__form-group-label pr-2 pl-3">
              {t('account.standardAccount')}
            </Label>
            <Field
              name="defaultAccount"
              component={renderCheckBoxField}
              className="colored-click"
            />
            <Label className="form__form-group-label pr-2 pl-3">
              {t('account.currentAccount')}
            </Label>
            <Field
              name="checkingAccount"
              component={renderCheckBoxField}
              className="colored-click"
              onChange={(event) => {
                if (event.target) {
                  setAccountChange(event.target.checked);
                }
              }}
            />
          </div>
        </div>
        <Collapse isOpen={accountChange} className="width100">
          <div className="form__form-group pt-3">
            <span className="form__form-group-label">{t('account.bank')}</span>
            <div className="form_form-group-field">
              <Field
                name="bank"
                type="text"
                component={Select}
                options={listOfBanks}
                onChange={(options) =>
                  setMaskers({
                    agencyPattern: options.agencyPattern,
                    accountPattern: options.accountPattern,
                  })
                }
                placeholder={t('account.placeholders.select')}
              />
            </div>
          </div>
          <div className="form__form-group--2_columns">
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('account.agency')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="agency"
                  type="text"
                  component={renderField}
                  maxLength="25"
                  placeholder={masker.agencyPattern || t('account.agency')}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('account.account')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="accountNumber"
                  type="text"
                  component={renderField}
                  maxLength="100"
                  placeholder={masker.accountPattern || t('account.account')}
                />
              </div>
            </div>
          </div>
          <div className="form__form-group--2_columns">
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('account.account-type')}
              </span>
              <div className="form_form-group-field">
                <Field
                  name="ownerType"
                  component={ownerTypeSelector}
                  onChange={(event, value) => {
                    setIsType(...value);
                    setOwnerId(!ownerId);
                  }}
                  color="primary"
                  t={t}
                />
              </div>
            </div>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t(`account.${isType}`)}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="ownerIdentifier"
                  type="text"
                  component={renderField}
                  placeholder={t('account.placeholders.number')}
                  {...(ownerId ? cnpjMasker() : cpfMasker())}
                />
              </div>
            </div>
          </div>
        </Collapse>
        <ButtonToolbar className="form__button-toolbar">
          <Button color="primary" onClick={backHandler}>
            {t('general.back')}
          </Button>
          <Button color="success" type="submit">
            {t('general.save')}
          </Button>
        </ButtonToolbar>
      </form>
    </Fragment>
  );
};

AccountForm.propTypes = {
  t: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  backHandler: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  initialize: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
};

export default reduxForm({
  form: 'AccountForm',
  validate,
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(withTranslation('common')(AccountForm));
